import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, AlertCircle, CheckCircle2, Clock, AlertTriangle, 
  Loader2, Send, ExternalLink, Copy, MessageSquare, RefreshCw,
  FileText, Zap, Rocket, User, Image as ImageIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth check hook for support pages
const useAuthCheck = (navigate) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      
      if (!token || !userInfo) {
        toast.error('Please login to access the Support Dashboard');
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userInfo);
        if (user.role !== 'internal_team' && user.role !== 'admin') {
          toast.error('Access denied. Internal team role required.');
          navigate('/login');
          return;
        }
        setIsAuthorized(true);
      } catch (e) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  return { isAuthorized, isChecking };
};

const statusConfig = {
  reported: { label: 'Reported', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
  diagnosing: { label: 'Diagnosing', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  fixing: { label: 'Applying Fix...', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  pending_approval: { label: 'Awaiting Approval', color: 'bg-amber-100 text-amber-800', icon: Clock },
  fixed: { label: 'Fixed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  deployed: { label: 'Deployed', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  verified: { label: 'Verified', color: 'bg-emerald-200 text-emerald-900', icon: CheckCircle2 },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  closed: { label: 'Closed', color: 'bg-gray-200 text-gray-700', icon: CheckCircle2 }
};

const actionIcons = {
  issue_reported: AlertCircle,
  diagnosis_started: Zap,
  diagnosis_completed: FileText,
  fix_applied: CheckCircle2,
  fix_deployed: Rocket,
  status_updated: RefreshCw,
  escalated: AlertTriangle,
  auto_escalated: AlertTriangle,
  note_added: MessageSquare,
  link_resent: Send,
  diagnosis_error: AlertCircle,
  ai_analysis_completed: Zap,
  auto_fix_started: Zap,
  ai_code_fix_applied: CheckCircle2,
  ai_fix_rolled_back: AlertTriangle,
  ai_fix_failed: AlertCircle,
  ai_retry_requested: RefreshCw,
  ai_retry_failed: AlertCircle,
  ai_fallback_triggered: Zap,
  ai_db_fix_applied: CheckCircle2,
  ai_db_fix_failed: AlertCircle,
  ai_recommendation: FileText,
  ai_cannot_auto_fix: AlertTriangle,
  ai_fix_pending_approval: Clock,
  fix_approved: CheckCircle2,
  fix_rejected: AlertTriangle
};

const categoryLabels = {
  unable_to_load: 'Unable to Load Assessment',
  answers_wiped: 'Answers/Progress Wiped',
  email_not_received: 'Email Not Received',
  wrong_assessment_content: 'Wrong Assessment Content',
  login_error: 'Login Error',
  progress_not_saving: 'Progress Not Saving',
  display_error: 'Display/UI Error',
  missing_from_system: 'Missing from System',
  other: 'Other'
};

export default function IssueDetail() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const { isAuthorized, isChecking } = useAuthCheck(navigate);
  
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [retryingAiFix, setRetryingAiFix] = useState(false);
  const [approvingFix, setApprovingFix] = useState(false);
  const [rejectingFix, setRejectingFix] = useState(false);

  const token = localStorage.getItem('token');

  const fetchIssue = useCallback(async () => {
    if (!isAuthorized) return;
    
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIssue(data);
      } else {
        toast.error('Issue not found');
        navigate('/support/issues');
      }
    } catch (error) {
      console.error('Error fetching issue:', error);
      toast.error('Failed to load issue details');
    } finally {
      setLoading(false);
    }
  }, [issueId, token, navigate, isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) return;
    
    fetchIssue();
    
    // Poll for updates if status is diagnosing or fixing
    const interval = setInterval(() => {
      if (issue?.status === 'diagnosing' || issue?.status === 'fixing') {
        fetchIssue();
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [fetchIssue, issue?.status, isAuthorized]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't render if not authorized (will redirect)
  if (!isAuthorized) {
    return null;
  }

  const handleMarkVerified = async () => {
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'verified' })
      });

      if (response.ok) {
        toast.success('Issue marked as verified');
        fetchIssue();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEscalate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}/escalate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Issue escalated');
        fetchIssue();
      }
    } catch (error) {
      toast.error('Failed to escalate issue');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    setSubmittingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}/add-note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: noteText })
      });

      if (response.ok) {
        toast.success('Note added');
        setNoteText('');
        fetchIssue();
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleRetryAiFix = async () => {
    setRetryingAiFix(true);
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}/retry-ai-fix`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.fix_applied) {
          toast.success('AI successfully applied a fix!');
        } else {
          toast.info('AI could not auto-fix this issue. It has been escalated.');
        }
        fetchIssue();
      } else {
        toast.error('Failed to retry AI fix');
      }
    } catch (error) {
      toast.error('Error retrying AI fix');
    } finally {
      setRetryingAiFix(false);
    }
  };

  const handleApproveFix = async () => {
    setApprovingFix(true);
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}/approve-fix`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.applied) {
          toast.success('Fix approved and applied successfully!');
        } else {
          toast.error('Fix was approved but could not be applied.');
        }
        fetchIssue();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to approve fix');
      }
    } catch (error) {
      toast.error('Error approving fix');
    } finally {
      setApprovingFix(false);
    }
  };

  const handleRejectFix = async () => {
    setRejectingFix(true);
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}/reject-fix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Rejected by reviewer' })
      });

      if (response.ok) {
        toast.success('Fix rejected. Issue escalated for manual review.');
        fetchIssue();
      } else {
        toast.error('Failed to reject fix');
      }
    } catch (error) {
      toast.error('Error rejecting fix');
    } finally {
      setRejectingFix(false);
    }
  };

  const handleResendLink = async () => {
    try {
      const response = await fetch(`${API_URL}/api/support/issues/${issueId}/resend-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Assessment link ready: ${data.assessmentLink}`);
        
        // Copy to clipboard
        navigator.clipboard.writeText(`${window.location.origin}${data.assessmentLink}`);
        toast.info('Link copied to clipboard');
        
        fetchIssue();
      }
    } catch (error) {
      toast.error('Failed to get assessment link');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.reported;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} font-normal flex items-center gap-1`}>
        <Icon className={`w-3 h-3 ${status === 'diagnosing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Issue not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="issue-detail">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/support/issues')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Issues
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">Issue #{issue.id.substring(0, 8)}</h1>
                {getStatusBadge(issue.status)}
              </div>
              <p className="text-gray-600">
                Reported by {issue.reportedBy} on {formatDateTime(issue.createdAt)}
              </p>
            </div>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchIssue}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Employee Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Employee Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{issue.employeeName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{issue.employeeEmail || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <p className="font-medium">{issue.employeeRole || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Region</p>
                <p className="font-medium">{issue.employeeRegion || '-'}</p>
              </div>
            </div>
            
            {issue.assessmentLink && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Assessment Link</p>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-100 px-3 py-1 rounded text-sm flex-1">
                    {issue.assessmentLink}
                  </code>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => copyToClipboard(`${window.location.origin}${issue.assessmentLink}`)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => window.open(issue.assessmentLink, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Issue Details Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Issue Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">Category</p>
              <Badge variant="outline" className="text-base">
                {categoryLabels[issue.issueCategory] || issue.issueCategory}
              </Badge>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Description</p>
              <p className="text-gray-800 whitespace-pre-wrap">{issue.issueDescription}</p>
            </div>

            {issue.screenshotData && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowScreenshot(!showScreenshot)}
                  className="mb-2"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  {showScreenshot ? 'Hide' : 'Show'} Screenshot
                </Button>
                {showScreenshot && (
                  <div className="mt-2 border rounded-lg p-2">
                    <img 
                      src={issue.screenshotData} 
                      alt="Issue screenshot" 
                      className="max-w-full rounded"
                    />
                    <p className="text-xs text-gray-500 mt-2">{issue.screenshotFilename}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AUTO-FIX SUCCESS: Show what AI automatically fixed */}
        {issue.status === 'deployed' && issue.fixApplied?.action?.includes('auto_fix') && (
          <Card className="mb-6 border-green-300 bg-green-50" data-testid="auto-fix-success-panel">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <Zap className="w-5 h-5" />
                  AI Auto-Fix Applied
                </CardTitle>
                <Badge className="bg-green-200 text-green-900">
                  {issue.fixApplied.confidence || 'auto'} confidence
                </Badge>
              </div>
              <CardDescription className="text-green-700">
                The AI diagnosed and automatically fixed this issue without manual intervention.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {issue.fixApplied.root_cause && (
                <div className="p-3 bg-white rounded border border-green-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Root Cause</p>
                  <p className="text-sm text-gray-600">{issue.fixApplied.root_cause}</p>
                </div>
              )}
              {issue.fixApplied.fix_details && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Changes Applied ({issue.fixApplied.fix_details.length})</p>
                  <div className="space-y-2">
                    {issue.fixApplied.fix_details.map((d, i) => (
                      <div key={i} className="bg-white rounded border border-green-200 p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <code className="text-xs text-gray-600">{d.file}</code>
                        </div>
                        <p className="text-sm text-gray-700">{d.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {issue.fixApplied.verification && (
                <div className="p-3 bg-white rounded border border-green-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Verification</p>
                  <p className="text-sm text-gray-600">{issue.fixApplied.verification}</p>
                </div>
              )}
              {issue.fixApplied.services_restarted?.length > 0 && (
                <p className="text-xs text-green-700">
                  Services restarted: {issue.fixApplied.services_restarted.join(', ')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* ROLLBACK/FAILURE: Show what went wrong if auto-fix failed */}
        {issue.status === 'escalated' && issue.fixApplied?.action?.includes('rolled_back') && (
          <Card className="mb-6 border-red-300 bg-red-50" data-testid="auto-fix-rollback-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                Auto-Fix Rolled Back
              </CardTitle>
              <CardDescription className="text-red-700">
                The AI applied a fix but it caused a service issue. All changes were automatically reverted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-800">{issue.fixApplied.message}</p>
              {issue.fixApplied.root_cause && (
                <div className="mt-3 p-3 bg-white rounded border border-red-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Root Cause Identified</p>
                  <p className="text-sm text-gray-600">{issue.fixApplied.root_cause}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* PENDING APPROVAL: Legacy — shown only if somehow in pending_approval state */}
        {issue.status === 'pending_approval' && issue.fixApplied?.proposed_fixes && (
          <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="proposed-fix-panel">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Clock className="w-5 h-5" />
                  AI Proposed Fix — Review Required
                </CardTitle>
                <Badge className="bg-amber-200 text-amber-900">
                  {issue.fixApplied.confidence || 'unknown'} confidence
                </Badge>
              </div>
              <CardDescription className="text-amber-700">
                The AI has analyzed the issue and generated code changes. Review them below before approving.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Root Cause */}
              {issue.fixApplied.root_cause && (
                <div className="p-3 bg-white rounded border border-amber-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Root Cause</p>
                  <p className="text-sm text-gray-600">{issue.fixApplied.root_cause}</p>
                </div>
              )}

              {/* Proposed Code Changes */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Proposed Code Changes ({issue.fixApplied.proposed_fixes.length})
                </p>
                <div className="space-y-3">
                  {issue.fixApplied.proposed_fixes.map((fix, i) => (
                    <div key={i} className="bg-white rounded border border-amber-200 overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                        <code className="text-xs text-gray-600">{fix.file_path}</code>
                        <Badge variant="outline" className="text-xs">{fix.action}</Badge>
                      </div>
                      <div className="p-3">
                        <p className="text-sm text-gray-700 mb-2 font-medium">{fix.description}</p>
                        <div className="grid grid-cols-1 gap-2">
                          <div>
                            <p className="text-xs text-red-600 font-medium mb-1">Remove:</p>
                            <pre className="text-xs bg-red-50 p-2 rounded border border-red-100 overflow-x-auto whitespace-pre-wrap max-h-40">
                              {fix.search?.substring(0, 500)}{fix.search?.length > 500 ? '...' : ''}
                            </pre>
                          </div>
                          <div>
                            <p className="text-xs text-green-600 font-medium mb-1">Add:</p>
                            <pre className="text-xs bg-green-50 p-2 rounded border border-green-100 overflow-x-auto whitespace-pre-wrap max-h-40">
                              {fix.replace?.substring(0, 500)}{fix.replace?.length > 500 ? '...' : ''}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Proposed Database Changes */}
              {issue.fixApplied.proposed_db_fixes?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Proposed Database Changes ({issue.fixApplied.proposed_db_fixes.length})
                  </p>
                  {issue.fixApplied.proposed_db_fixes.map((dbFix, i) => (
                    <div key={i} className="bg-white rounded border border-amber-200 p-3 mb-2">
                      <p className="text-sm text-gray-700 mb-1">{dbFix.description}</p>
                      <pre className="text-xs bg-gray-50 p-2 rounded border overflow-x-auto">{dbFix.sql}</pre>
                    </div>
                  ))}
                </div>
              )}

              {/* Verification */}
              {issue.fixApplied.verification && (
                <div className="p-3 bg-white rounded border border-amber-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">How to Verify</p>
                  <p className="text-sm text-gray-600">{issue.fixApplied.verification}</p>
                </div>
              )}

              {/* Approve / Reject Buttons */}
              <div className="flex gap-3 pt-3 border-t border-amber-200">
                <Button
                  onClick={handleApproveFix}
                  disabled={approvingFix}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                  data-testid="approve-fix-btn"
                >
                  {approvingFix ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {approvingFix ? 'Applying Fix...' : 'Approve & Apply Fix'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRejectFix}
                  disabled={rejectingFix}
                  className="flex-1"
                  data-testid="reject-fix-btn"
                >
                  {rejectingFix ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 mr-2" />
                  )}
                  Reject Fix
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fix Proof Card (if available) */}
        {issue.fixProof && issue.status !== 'pending_approval' && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <CheckCircle2 className="w-5 h-5" />
                Resolution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-green-900 font-medium">{issue.fixProof}</p>
              
              {issue.fixApplied?.files_modified && (
                <div className="mt-3 p-3 bg-white rounded border border-green-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Files Modified:</p>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {issue.fixApplied.files_modified.map((f, i) => (
                      <li key={i}><code className="bg-gray-100 px-1 rounded text-xs">{f}</code></li>
                    ))}
                  </ul>
                </div>
              )}

              {issue.fixApplied?.fix_details && (
                <div className="mt-3 p-3 bg-white rounded border border-green-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Changes Applied:</p>
                  {issue.fixApplied.fix_details.map((d, i) => (
                    <div key={i} className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">{i + 1}.</span> {d.description}
                    </div>
                  ))}
                </div>
              )}

              {issue.fixApplied?.services_restarted && issue.fixApplied.services_restarted.length > 0 && (
                <p className="mt-2 text-xs text-green-700">
                  Services restarted: {issue.fixApplied.services_restarted.join(', ')}
                </p>
              )}
              
              {issue.fixApplied?.new_link && (
                <div className="mt-4 p-3 bg-white rounded border border-green-200">
                  <p className="text-sm text-gray-600 mb-1">New Assessment Link:</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-gray-100 px-3 py-1 rounded text-sm flex-1">
                      {issue.fixApplied.new_link}
                    </code>
                    <Button 
                      size="sm"
                      onClick={() => copyToClipboard(`${window.location.origin}${issue.fixApplied.new_link}`)}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Activity Timeline */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Activity Timeline</CardTitle>
            <CardDescription>Complete history of diagnosis and resolution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              
              <div className="space-y-6">
                {issue.activityLog?.map((log, index) => {
                  const Icon = actionIcons[log.action] || MessageSquare;
                  return (
                    <div key={log.id} className="relative pl-10" data-testid={`activity-${index}`}>
                      {/* Timeline dot */}
                      <div className="absolute left-2 w-5 h-5 bg-white rounded-full border-2 border-blue-500 flex items-center justify-center">
                        <Icon className="w-3 h-3 text-blue-500" />
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 capitalize">
                            {log.action.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(log.createdAt)}
                          </span>
                        </div>
                        
                        {log.details && (
                          <div className="text-sm text-gray-600">
                            {typeof log.details === 'object' ? (
                              <div className="space-y-1">
                                {log.details.root_cause && (
                                  <p><span className="font-medium">Root Cause:</span> {log.details.root_cause}</p>
                                )}
                                {log.details.description && typeof log.details.description === 'string' && (
                                  <p>{log.details.description}</p>
                                )}
                                {Array.isArray(log.details.description) && (
                                  <ul className="list-disc list-inside">
                                    {log.details.description.map((d, i) => <li key={i}>{d}</li>)}
                                  </ul>
                                )}
                                {log.details.files_modified && (
                                  <p><span className="font-medium">Files:</span> {log.details.files_modified.join(', ')}</p>
                                )}
                                {log.details.fixes_applied && (
                                  <p><span className="font-medium">Fixes Applied:</span> {log.details.fixes_applied}</p>
                                )}
                                {log.details.reason && (
                                  <p><span className="font-medium">Reason:</span> {log.details.reason}</p>
                                )}
                                {log.details.note && (
                                  <p className="italic">{log.details.note}</p>
                                )}
                                {log.details.confidence && (
                                  <p><span className="font-medium">Confidence:</span> {log.details.confidence}</p>
                                )}
                                {log.details.message && (
                                  <p>{log.details.message}</p>
                                )}
                                {log.details.error && (
                                  <p className="text-red-600">{log.details.error}</p>
                                )}
                                {log.details.manual_steps && (
                                  <p><span className="font-medium">Manual Steps:</span> {log.details.manual_steps}</p>
                                )}
                                {log.details.proof && (
                                  <p className="text-green-700 font-medium">{log.details.proof}</p>
                                )}
                                {/* Fallback: show raw for fields we didn't handle */}
                                {!log.details.root_cause && !log.details.description && !log.details.files_modified && 
                                 !log.details.reason && !log.details.note && !log.details.message && !log.details.proof && 
                                 !log.details.error && !log.details.manual_steps && !log.details.confidence && (
                                  <pre className="whitespace-pre-wrap bg-white p-2 rounded border text-xs overflow-x-auto">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                )}
                              </div>
                            ) : (
                              <p>{log.details}</p>
                            )}
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-400 mt-2">
                          by {log.performedBy}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Note */}
            <div>
              <p className="text-sm text-gray-500 mb-2">Add a note</p>
              <div className="flex gap-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add additional context or notes..."
                  rows={2}
                  className="flex-1"
                  data-testid="note-textarea"
                />
                <Button 
                  onClick={handleAddNote}
                  disabled={submittingNote || !noteText.trim()}
                  data-testid="add-note-btn"
                >
                  {submittingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              {(issue.status === 'escalated' || issue.status === 'reported') && (
                <Button 
                  onClick={handleRetryAiFix}
                  disabled={retryingAiFix}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="retry-ai-fix-btn"
                >
                  {retryingAiFix ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  {retryingAiFix ? 'AI Analyzing & Fixing...' : 'Retry AI Auto-Fix'}
                </Button>
              )}

              {issue.status === 'deployed' && (
                <Button 
                  onClick={handleMarkVerified}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="verify-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark as Verified
                </Button>
              )}
              
              {!['verified', 'closed', 'escalated'].includes(issue.status) && (
                <Button 
                  variant="destructive"
                  onClick={handleEscalate}
                  data-testid="escalate-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Escalate
                </Button>
              )}
              
              {issue.candidateId && (
                <Button 
                  variant="outline"
                  onClick={handleResendLink}
                  data-testid="resend-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Get Assessment Link
                </Button>
              )}
              
              <Button 
                variant="outline"
                onClick={() => navigate('/support/issues/new', {
                  state: {
                    candidateId: issue.candidateId,
                    employeeName: issue.employeeName,
                    employeeEmail: issue.employeeEmail,
                    employeeRole: issue.employeeRole,
                    employeeRegion: issue.employeeRegion,
                    assessmentLink: issue.assessmentLink
                  }
                })}
                data-testid="new-issue-btn"
              >
                Report New Issue for Same Employee
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
